import type { HttpErrorResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DiSocketApiUrl } from '@di';
import { FoerchGsmLocalStorageService } from '@services/foerch-gsm-local-storage.service';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import type {
  PubSubAckResponse,
  PubSubMessageResponse,
  PubSubSendToGroup,
  PubSubSystemResponse,
} from './azure-pubsub.model';
import { PubSubMessageType } from './azure-pubsub.model';

/**
 * ######################
 * #      NOTE          #
 * ######################
 *
 * This service is a copy from the web socket service of the web shop.
 * Adjustments of the service logic and enums must be done there too.
 *
 */

export enum WebSocketDataType {
  EMPTY = 'EMPTY',
  LOAD_CART = 'LOAD_CART',
  LOAD_ORDER = 'LOAD_ORDER',
  SWITCH_B2B_UNIT = 'SWITCH_B2B_UNIT',
  DOWNLOAD_FILE_READY = 'DOWNLOAD_FILE_READY',
}

export interface WebSocketData {
  type: WebSocketDataType;
  payload: any;
}

@Injectable()
export class FoerchWebSocketService {
  private readonly _http = inject(HttpClient);
  private readonly _localStorageService = inject(FoerchGsmLocalStorageService);
  private readonly _notificationBaseUrl = inject(DiSocketApiUrl);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _websocketProtocols = 'json.webpubsub.azure.v1';

  private _groupId: string;
  private _webSocketUrl: string;
  private _socket: WebSocket;
  private _lastPayload: any = null;
  private _isConnectionRequestPending = false;

  private _messages$: BehaviorSubject<WebSocketData> = new BehaviorSubject({
    type: WebSocketDataType.EMPTY,
    payload: null,
  });

  constructor() {
    this.connectIfNotConnected().then(() => {});
  }

  private get _isSocketOpen(): boolean {
    return !!this._socket && this._socket.CLOSED !== this._socket.readyState;
  }

  /**
   * This will simply send messages to server so that all instances with the
   * same groupId can receive this.
   *
   * Send message only on adding or removing data, and on listenDataChanges
   * refresh data only, to avoid redirects.
   */
  sendMessage(ev: WebSocketData): void {
    if (!this._socket) {
      return;
    }

    this.connectIfNotConnected().then(() => {
      const payload: PubSubSendToGroup = {
        type: 'sendToGroup',
        group: this._groupId,
        dataType: 'json',
        data: ev,
      };

      this._socket.send(JSON.stringify(payload));

      // remember last payload in order to identify the response to this message
      this._lastPayload = ev;
    });
  }

  getEvents(): BehaviorSubject<WebSocketData> {
    return this._messages$;
  }

  disconnect(): void {
    if (this._socket) {
      this._socket.close();
    }
    this._socket = null;
  }

  async connectIfNotConnected(): Promise<void> {
    return new Promise<void>((resolve, _reject) => {
      if (this._isSocketOpen === false) {
        const token = this._localStorageService.getAccessToken();
        this.connectWebSocket(token, resolve);
      } else {
        this.waitForConnection(0, resolve);
      }
    });
  }

  /**
   * Retrieve URL from service and connect web socket
   *
   * @param token Access token of current user
   * @param resolve Callback
   */
  private connectWebSocket(token: string, resolve): void {
    this._localStorageService
      .isUserLoggedIn()
      .pipe(takeUntilDestroyed(this._destroyRef), take(1))
      .subscribe((isLoggedIn: boolean) => {
        if (isLoggedIn && !this._isConnectionRequestPending) {
          this._isConnectionRequestPending = true;
          const uri = `${this._notificationBaseUrl}/getConnectionDetails?token=${token}`;

          this._http
            .get<{ userId: string; groupId: string; url: string }>(uri)
            .pipe(takeUntilDestroyed(this._destroyRef), take(1))
            .subscribe({
              next: ({ groupId, url }) => {
                this._groupId = groupId;
                this._webSocketUrl = url;

                this._socket = new WebSocket(
                  this._webSocketUrl,
                  this._websocketProtocols
                );

                this.attachEvents();

                this.waitForConnection(0, resolve);
                this._isConnectionRequestPending = false;
              },
              error: (err: HttpErrorResponse) => {
                console.error(
                  'Error while connecting to notification micro service:',
                  err.error.message
                );
              },
            });
        }
      });
  }

  /**
   * Should only wait if state is connecting, otherwise should resolve
   *
   * @param maxConnectionTried Number of connection tries
   * @param resolve callback
   */
  private waitForConnection(maxConnectionTried = 0, resolve: any): void {
    maxConnectionTried++;
    if (
      this._socket &&
      this._socket.CONNECTING === this._socket.readyState &&
      maxConnectionTried <= 5
    ) {
      setTimeout(() => {
        this.waitForConnection(maxConnectionTried, resolve);
      }, 3000);
    } else {
      resolve();
    }
  }

  /**
   * Register event handler for several web-socket events
   */
  private attachEvents(): void {
    // event listener for message event
    this._socket.addEventListener('message', this.onMessage.bind(this));

    // event listener for error event
    // reset socket on error
    this._socket.addEventListener('error', () => (this._socket = null));
  }

  private onMessage(ev: any): void {
    const payload:
      | PubSubMessageResponse
      | PubSubAckResponse
      | PubSubSystemResponse = JSON.parse(ev.data);

    switch (payload.type) {
      case PubSubMessageType.MESSAGE: {
        const data: WebSocketData = payload.data;
        // Process message but only if its payload differs from the last
        // sent payload.
        // This check is necessary because otherwise it is possible that some
        // requests are sent out twice:
        // Source instance: Cart change triggered
        //    -> cart gets reloaded and WebSocket method sent
        // The message sent will also be received by the source instance
        //    -> it reloads the cart for a second time
        if (JSON.stringify(data) !== JSON.stringify(this._lastPayload)) {
          this._messages$.next(data);
        }

        // reset lastPayload so that a similar message from another instance
        // will be processed again
        this._lastPayload = null;
        break;
      }

      case PubSubMessageType.ACK: {
        if (!payload.success) {
          console.error('WebSocket ACK error:', payload.error.message);
        }
        break;
      }

      case PubSubMessageType.SYSTEM: {
        // Commented code has been left here
        // in case we want to react on system messages later
        /*
        if (payload.event === PubSubSystemEventEnum.CONNECTED) {
          // tslint:disable-next-line:no-console
          console.info('Connected to PubSub');
        } else {
          // tslint:disable-next-line:no-console
          console.info('Disconnected from PubSub:', payload.message);
        }
        */
        break;
      }
    }
  }
}
